import { useEffect, useState } from 'react';
import {
  CreditCard,
  QrCode,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ReceiptText,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MoneyValue } from '../MoneyValue';
import { useEdcSocket } from '@/hooks/useEdcSocket';
import { resolveEdcTransaction, stopWaitingForEdc } from '@/api/edc.api';
import type { SocketConfig } from '@/types/sale';
import type { ApiError } from '@/types/api';
import type { EdcState } from '@/types/payment';
import { edcCopyFor, type EdcTender } from './edcTenderCopy';
import { isTerminalLive } from './terminalLive';
import { PREVIEW_EVIDENCE } from './edcPreviewEvidence';

interface EdcPanelProps {
  total: number;
  /** Which tender this terminal was armed for. Defaults to card. */
  tender?: EdcTender;
  /**
   * Reports whether the terminal is still holding a live transaction, so the
   * dialog can refuse to be dismissed while a customer can still pay. The
   * protocol has no cancel, so closing this screen does NOT stop the terminal.
   */
  onTerminalLiveChange?: (live: boolean) => void;
  socketConfig: SocketConfig | null;
  /** PaymentTransaction id — the handle for resolving a lost response. */
  paymentTransactionId: string | null;
  onSuccess: () => void;
  onCancel: () => void;
  /**
   * Retry this cart. Receives the SERVER's verdict on whether a genuinely new
   * charge is safe — the caller needs it because only it holds the
   * idempotency key. Undefined means not known, which is treated as not safe.
   */
  onRetry: (retrySafe?: boolean) => void;
  /**
   * DEV ONLY — force a phase so the flow can be reviewed without a terminal.
   * Some phases (notably `unresolved`, which needs a terminal that accepts a
   * card and then goes silent) are otherwise near-impossible to reproduce.
   * `import.meta.env.DEV` is compile-time, so this is stripped from a
   * production build entirely.
   */
  __previewPhase?: EdcState['phase'];
}

/** The receipt trace number is exactly six digits (spec §7.2, an6 numeric). */
const TRACE_PATTERN = /^\d{6}$/;

/**
 * Card OR QRIS payment at the Permata EDC terminal.
 *
 * One terminal serves both tenders and the dialog picks this panel off the
 * GATEWAY (`edc-permata`), which is the same for each — so the tender has to be
 * passed in explicitly or every screen reads as a card payment. See
 * `edcTenderCopy.ts` for why that matters on the unresolved screen.
 *
 * Mirrors EmoneyPanel's shape so a cashier who knows one knows the other, with
 * one screen that has no equivalent anywhere else in the app: the resolution
 * prompt shown when the terminal never answered.
 */
export function EdcPanel({
  total,
  tender,
  onTerminalLiveChange,
  socketConfig,
  paymentTransactionId,
  onSuccess,
  onCancel,
  onRetry,
  __previewPhase,
}: EdcPanelProps) {
  const copy = edcCopyFor(tender);
  const live = useEdcSocket({ config: socketConfig, open: Boolean(socketConfig) });
  const state: EdcState =
    import.meta.env.DEV && __previewPhase
      ? {
          phase: __previewPhase,
          panMasking: '460502******1234',
          approvalCode: '000000',
          ...PREVIEW_EVIDENCE[__previewPhase],
        }
      : live.state;

  const terminalLive = isTerminalLive(state.phase);
  useEffect(() => {
    onTerminalLiveChange?.(terminalLive);
  }, [terminalLive, onTerminalLiveChange]);

  // Whatever happens, stop claiming the terminal is busy once this unmounts —
  // otherwise a dialog closed on an outcome would leave the guard latched on.
  useEffect(() => () => onTerminalLiveChange?.(false), [onTerminalLiveChange]);

  useEffect(() => {
    if (import.meta.env.DEV && __previewPhase) return;
    if (state.phase === 'approved') {
      const t = setTimeout(onSuccess, 900);
      return () => clearTimeout(t);
    }
  }, [state.phase, onSuccess, __previewPhase]);

  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="rounded-lg bg-secondary px-6 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-600">Total tagihan</p>
        <MoneyValue value={total} emphasis className="text-3xl" />
      </div>

      {(state.phase === 'connecting' || state.phase === 'waiting_card') && (
        <div className="flex flex-col items-center gap-3">
          <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-primary/10">
            <span className="absolute inset-0 animate-ping rounded-full bg-primary/15" />
            {/* Only this icon is tender-aware, deliberately: it is the one
                giving an INSTRUCTION, and a card drawn at a customer who was
                asked to scan a QR tells them to do the wrong thing. The
                outcome icons downstream read as check / cross / clock, not as
                the instrument, so they stay shared. */}
            {tender === 'edc-qris' ? (
              <QrCode className="h-12 w-12 text-primary" />
            ) : (
              <CreditCard className="h-12 w-12 text-primary" />
            )}
          </div>
          <p className="font-display text-lg font-semibold text-ink-900">
            {state.phase === 'connecting' ? 'Menghubungkan mesin EDC…' : copy.waitingTitle}
          </p>
          <p className="text-sm text-muted-foreground">
            {copy.waitingBody}
          </p>
        </div>
      )}

      {state.phase === 'processing' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-14 w-14 animate-spin text-primary" />
          <p className="font-display text-lg font-semibold text-ink-900">Memproses di mesin EDC…</p>
          <p className="text-sm text-muted-foreground">{copy.processingBody}</p>
        </div>
      )}

      {state.phase === 'approved' && (
        <div className="flex animate-gold-pulse flex-col items-center gap-2 rounded-full p-4">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <p className="font-display text-lg font-semibold text-success">Pembayaran berhasil</p>
          {state.panMasking && (
            <p className="font-mono text-sm text-ink-600">{state.panMasking}</p>
          )}
          {state.approvalCode && (
            <p className="text-xs text-muted-foreground">Kode approval {state.approvalCode}</p>
          )}
        </div>
      )}

      {state.phase === 'declined' && (
        <div className="flex flex-col items-center gap-2">
          <XCircle className="h-14 w-14 text-destructive" />
          <p className="font-display text-lg font-semibold text-destructive">Pembayaran ditolak</p>
          <p className="text-sm text-muted-foreground">
            {state.reason ?? 'Mesin EDC menolak transaksi ini.'}
          </p>
          <p className="text-sm text-ink-600">{copy.declinedBody}</p>
        </div>
      )}

      {state.phase === 'error' && (
        <div className="flex flex-col items-center gap-2">
          <AlertTriangle className="h-14 w-14 text-warning" />
          <p className="font-display text-lg font-semibold text-ink-900">Status tidak terpantau</p>
          <p className="text-sm text-muted-foreground">{state.message}</p>
        </div>
      )}

      {/* The cancel instruction belongs to EVERY live phase, not just the
          waiting one. It is driven by the SAME predicate that makes the dialog
          refuse to close, so the screen can never again say "you cannot leave"
          without also saying where the exit is. That divergence was real: a
          QRIS payment goes straight to `processing`, so the hint sat on a
          screen that tender never shows. */}
      {terminalLive && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs text-muted-foreground/80">{copy.cancelHint}</p>
          {offersStopWaiting(tender) && (
            <StopWaiting paymentTransactionId={paymentTransactionId} />
          )}
        </div>
      )}

      {state.phase === 'unresolved' && (
        <EdcResolutionPrompt
          paymentTransactionId={paymentTransactionId}
          reason={state.reason}
          tender={tender}
          onSettled={onSuccess}
          onFailed={onCancel}
        />
      )}

      {/* No cancel button while the terminal has the card: the protocol has no
          cancel message, so a button here could not do what it says. */}
      {state.phase !== 'unresolved' && (
        <div className="flex w-full justify-center gap-2 pt-2">
          {(state.phase === 'declined' || state.phase === 'error') && (
            <>
              <Button variant="outline" onClick={onCancel}>
                Batalkan
              </Button>
              <Button onClick={() => onRetry(live.retrySafe)}>Coba lagi</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * How long a card wait has to run before we offer a way out of it.
 *
 * Roughly half the card response window, and measured rather than picked: on
 * real hardware card payments have completed anywhere from 7.7s to 85.6s, with
 * one landing at 30.5s — which is how the first draft got caught. At 30s the
 * control appeared for half a second on a perfectly normal payment and then
 * vanished, and a control that flickers teaches a cashier to distrust the
 * screen.
 */
const STOP_WAITING_AFTER_MS = 45_000;

/**
 * Card only, deliberately.
 *
 * A card is physically in the slot: the cashier can see that the customer has
 * gone, and the terminal is the only thing still holding the sale. A QRIS
 * payment is someone with a phone in their hand, and from the counter there is
 * no way to tell "walked off" from "still finding their banking app" — so a way
 * out offered there mostly invites abandoning payments that were about to
 * land. The QRIS escape stays what it already was and what the hint above says:
 * press CANCEL on the terminal, which is a real cancel and answers in seconds.
 */
function offersStopWaiting(tender?: EdcTender) {
  return tender !== 'edc-qris';
}

/**
 * The way out of a wait that has stopped being normal.
 *
 * This screen's whole position is that there is no cancel button, because the
 * protocol has none — a transaction is cancelled at the terminal's keypad or
 * not at all. That stays true. What this offers is narrower and it is named for
 * what it actually does: stop WAITING. The terminal keeps the payment.
 *
 * Deliberately absent for the first 30 seconds. Card payments routinely take
 * 20-40 seconds on real hardware, so a control offered from the outset would
 * invite a cashier to abandon payments that were about to succeed. It appears
 * once waiting has become unusual, which is the only moment it is the right
 * answer.
 *
 * Two steps, because one tap costs something real: after this the backend stops
 * reading the terminal's reply, so the printed slip becomes the only authority.
 * The confirm says so rather than asking "are you sure?", which tells a cashier
 * nothing they can weigh.
 */
function StopWaiting({ paymentTransactionId }: { paymentTransactionId: string | null }) {
  const [visible, setVisible] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), STOP_WAITING_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  if (!visible || !paymentTransactionId) return null;

  async function stop() {
    setBusy(true);
    try {
      await stopWaitingForEdc(paymentTransactionId as string);
      // No navigation here: the backend parks the row and pushes `unresolved`
      // down the socket, which is what moves this dialog to the prompt. Doing
      // it locally as well would race that and could show the prompt for a
      // payment the server had refused to park.
    } catch (error) {
      const apiErr = error as ApiError;
      toast.error(apiErr?.message ?? 'Tidak bisa berhenti menunggu. Coba lagi.');
      setBusy(false);
      setConfirming(false);
    }
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="text-xs text-muted-foreground underline underline-offset-4 hover:text-ink-900"
      >
        Berhenti menunggu
      </button>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 rounded-md bg-secondary px-4 py-3">
      <p className="max-w-xs text-xs text-ink-600">
        Mesin EDC tetap memproses pembayaran ini. Setelah berhenti, jawabannya tidak dibaca
        otomatis — Anda perlu membaca struk mesin.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setConfirming(false)} disabled={busy}>
          Kembali
        </Button>
        <Button size="sm" onClick={stop} disabled={busy}>
          {busy ? 'Menghentikan…' : 'Ya, berhenti'}
        </Button>
      </div>
    </div>
  );
}

/**
 * The terminal never answered.
 *
 * This is the highest-stakes screen in the app and it is deliberately unlike
 * every other one:
 *
 *  - It is AMBER, not red. Red means failed, and the entire point is that this
 *    is not a failure — the card may well have been charged. Styling it as an
 *    error would teach exactly the wrong reflex.
 *  - It does not spin. Nothing is loading; the system has stopped and a person
 *    has to look at a physical machine. Motion here would imply otherwise.
 *  - The two answers carry deliberately different friction. Saying "no" is one
 *    tap. Saying "yes, it approved" requires typing the six digits off the
 *    printed slip — which cannot be done without having the slip in hand. That
 *    friction is the control, not an inconvenience.
 *  - Each button states its consequence, so nobody has to guess which is safe.
 */
function EdcResolutionPrompt({
  paymentTransactionId,
  reason,
  tender,
  onSettled,
  onFailed,
}: {
  paymentTransactionId: string | null;
  reason?: string;
  /** Card or QRIS — decides whether we say "debited" or "payment came in". */
  tender?: EdcTender;
  onSettled: () => void;
  onFailed: () => void;
}) {
  const copy = edcCopyFor(tender);
  const [answer, setAnswer] = useState<'none' | 'approved' | 'declined'>('none');
  const [traceNo, setTraceNo] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const traceValid = TRACE_PATTERN.test(traceNo);
  const showTraceError = touched && !traceValid;

  async function submit(outcome: 'SETTLED' | 'FAILED') {
    if (!paymentTransactionId) {
      toast.error('Transaksi tidak dikenali. Hubungi supervisor.');
      return;
    }
    setBusy(true);
    try {
      await resolveEdcTransaction(paymentTransactionId, {
        outcome,
        receiptTraceNo: outcome === 'SETTLED' ? traceNo : undefined,
      });
      if (outcome === 'SETTLED') {
        toast.success('Pembayaran dicatat. Tiket akan dicetak.');
        onSettled();
      } else {
        toast.success('Pesanan dibatalkan.');
        onFailed();
      }
    } catch (error) {
      const apiErr = error as ApiError;
      toast.error(apiErr?.message ?? 'Gagal menyimpan. Coba lagi atau hubungi supervisor.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full rounded-xl border-2 border-warning/40 bg-warning/5 p-5 text-left">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-warning" aria-hidden="true" />
        <div>
          <h3 className="font-display text-lg font-semibold text-ink-900">
            Mesin EDC tidak memberi jawaban
          </h3>
          <p className="mt-1 text-sm text-ink-600">
            <strong>{copy.unresolvedBody}</strong> Jangan ulangi pembayaran sebelum
            memeriksa mesin.
          </p>
          {reason && <p className="mt-1 text-xs text-muted-foreground">({reason})</p>}
        </div>
      </div>

      <p className="mt-5 font-display text-base font-semibold text-ink-900">
        Lihat mesin EDC sekarang — apakah struk tercetak APPROVED?
      </p>

      {answer === 'none' && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setAnswer('approved')}
            // Deliberately the SAME visual weight as the other option. An
            // earlier pass tinted this one green, which read as "recommended" —
            // the last thing to suggest when the question is whether a customer
            // was charged. The icons carry the meaning; neither is a default.
            className="flex flex-col items-start gap-1 rounded-lg border-2 border-ink-200 bg-background p-4 text-left transition hover:border-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-400"
          >
            <span className="flex items-center gap-2 font-semibold text-ink-900">
              <ReceiptText className="h-5 w-5 text-success" aria-hidden="true" />
              Ya — struk APPROVED
            </span>
            <span className="text-xs text-muted-foreground">
              Perlu nomor trace dari struk. Tiket akan dicetak.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setAnswer('declined')}
            className="flex flex-col items-start gap-1 rounded-lg border-2 border-ink-200 bg-background p-4 text-left transition hover:border-ink-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-400"
          >
            <span className="flex items-center gap-2 font-semibold text-ink-900">
              <Ban className="h-5 w-5 text-ink-600" aria-hidden="true" />
              Tidak — tidak ada struk
            </span>
            <span className="text-xs text-muted-foreground">
              Atau struk tertulis DECLINE. Pesanan dibatalkan.
            </span>
          </button>
        </div>
      )}

      {answer === 'approved' && (
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="edc-trace">Nomor trace pada struk (6 digit)</Label>
            <Input
              id="edc-trace"
              inputMode="numeric"
              autoFocus
              maxLength={6}
              placeholder="000123"
              value={traceNo}
              onChange={(e) => setTraceNo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onBlur={() => setTouched(true)}
              aria-invalid={showTraceError}
              aria-describedby={showTraceError ? 'edc-trace-error' : 'edc-trace-hint'}
              className="mt-1 font-mono text-lg tracking-widest"
            />
            {showTraceError ? (
              <p id="edc-trace-error" className="mt-1 text-xs font-medium text-destructive">
                Masukkan 6 digit angka persis seperti tercetak pada struk.
              </p>
            ) : (
              <p id="edc-trace-hint" className="mt-1 text-xs text-muted-foreground">
                Tertulis sebagai TRACE atau NO. URUT pada struk mesin EDC.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAnswer('none')} disabled={busy}>
              Kembali
            </Button>
            <Button onClick={() => submit('SETTLED')} disabled={!traceValid || busy}>
              {busy ? 'Menyimpan…' : 'Catat sebagai lunas'}
            </Button>
          </div>
        </div>
      )}

      {answer === 'declined' && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-ink-600">
            Pesanan akan dibatalkan dan kursi dilepas. Pastikan mesin EDC benar-benar tidak
            mencetak struk APPROVED.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAnswer('none')} disabled={busy}>
              Kembali
            </Button>
            <Button variant="destructive" onClick={() => submit('FAILED')} disabled={busy}>
              {busy ? 'Menyimpan…' : 'Batalkan pesanan'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
